import { ReactElement, useMemo, useCallback } from 'react';
import { Stack } from '@mui/material';
import { SelectRowProps, TableChangeHandler } from 'react-bootstrap-table-next';

import {
  DataTable,
  DialogDetailsOrder,
  DialogConfimation,
  DialogCancelPartnerItem,
} from '~components/index';
import { useAppSelector, useValidatePartnerItems } from '~hooks/index';
import {
  useDataTableSelectedProps,
  SelectRowsHandlerOnSelectAll,
  SelectRowsHandlerOnSelect,
} from '~hooks/useDataTableSelectedProps';
import { selectAuth } from '~store/slices/auth/index';
import { ItemExtended } from '~services/item/types';
import { isOnlyReadUser } from '~utils/user';
import NoRecordsFoundMessage from '~app/components/DataTable/NoRecordsFoundMessage';

// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierCollectFiltersContext } from '../../../../../CollectFiltersContext';
import { useDashboardVariantV2CarrierContentCollectTabContext } from '../../Context';

import { ITEM_KEY_ID } from '../../utils';
import { DashboardVariantV2CarrierContentDataTableContainer } from '../../../../styles';

import { useCollectTabDataTableColumns } from './useColumns';
// eslint-disable-next-line max-len
import DashboardVariantV2CarrierContentCollectTabDataTableNoDataIndication from './NoDataIndication';

const DashboardVariantV2CarrierContentCollectTabDataTable =
  (): ReactElement => {
    const { user } = useAppSelector(selectAuth);

    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);

    const {
      filters: { isFilterApplied },
    } = useDashboardVariantV2CarrierCollectFiltersContext();

    const {
      parseFiltersRequest,
      selection: { selectedIds, utils: selectedIdsUtils },
      table: {
        data,
        loading: loadingRequestTable,
        totalSize,
        queryParams: { pagination },
        handleChangeTable: changeTable,
      },
      actions: { reloadDataAndResetSelectedRows },
      dialogs: {
        detailsOrder: {
          open: openDialogDetailsOrder,
          data: selectedDataForDetailsOrder,
          handleClose: handleCloseDialogDetailsOrder,
        },
        confirmValidateOrder: {
          open: openConfirmValidateOrder,
          data: selectedValidateOrder,
          handleClose: handleCloseConfirmValidateOrder,
        },
        cancelOrder: {
          open: openDialogCancelOrder,
          data: selectedCancelOrder,
          handleClose: handleCloseDialogCancelOrder,
        },
      },
    } = useDashboardVariantV2CarrierContentCollectTabContext();

    const columns = useCollectTabDataTableColumns();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChangeTable = useCallback<TableChangeHandler<any>>(
      (type, newState) => {
        changeTable(type, {
          pagination: newState,
          filters: parseFiltersRequest(),
        });
      },
      [changeTable, parseFiltersRequest],
    );

    const handleOnSelectAll = useCallback<
      SelectRowsHandlerOnSelectAll<ItemExtended>
    >(
      (isSelected, rows) => {
        if (isSelected) {
          const ids = rows.map((row) => row[ITEM_KEY_ID]);
          selectedIdsUtils.addMultiple(ids);
        } else {
          selectedIdsUtils.reset();
        }
      },
      [selectedIdsUtils],
    );

    const handleOnSelect = useCallback<SelectRowsHandlerOnSelect<ItemExtended>>(
      (row) => {
        const currentId = row[ITEM_KEY_ID];
        selectedIdsUtils.toggle(currentId);
      },
      [selectedIdsUtils],
    );

    const selectRowsProps = useDataTableSelectedProps({
      onSelectAll: handleOnSelectAll,
      onSelect: handleOnSelect,
      selectedIds,
    });

    const dataTableSelectRowProps = useMemo<
      SelectRowProps<ItemExtended> | undefined
    >(
      () => (isUserOnlyReader ? undefined : selectRowsProps),
      [isUserOnlyReader, selectRowsProps],
    );

    const getNoRecordsIndicator = useCallback(() => {
      if (isFilterApplied) return <NoRecordsFoundMessage />;

      return (
        <DashboardVariantV2CarrierContentCollectTabDataTableNoDataIndication />
      );
    }, [isFilterApplied]);

    const messageDialogConfimationValidateOrder = useMemo(
      () =>
        `El pedido ${selectedValidateOrder?.trackingCode} será marcado como recepcionado.`,
      [selectedValidateOrder],
    );

    const handleSuccessValidateOrders = useCallback(() => {
      handleCloseConfirmValidateOrder();

      reloadDataAndResetSelectedRows();
    }, [handleCloseConfirmValidateOrder, reloadDataAndResetSelectedRows]);

    const {
      loading: loadingValidateOrder,
      handleValidatePartnerItems: executeValidateOrder,
    } = useValidatePartnerItems(
      handleSuccessValidateOrders,
      selectedValidateOrder,
    );

    const handleValidateOrder = useCallback(() => {
      if (selectedValidateOrder)
        executeValidateOrder([selectedValidateOrder.itemId]);
    }, [selectedValidateOrder, executeValidateOrder]);

    return (
      <>
        <Stack>
          <DashboardVariantV2CarrierContentDataTableContainer fullWidth>
            <DataTable
              loading={loadingRequestTable}
              data={data}
              columns={columns}
              keyField={ITEM_KEY_ID}
              pagination={{
                options: {
                  sizePerPage: pagination.sizePerPage,
                  page: pagination.page,
                  totalSize,
                },
              }}
              remote={{
                pagination: true,
              }}
              onTableChange={handleChangeTable}
              selectRow={dataTableSelectRowProps}
              noDataIndicationComponent={getNoRecordsIndicator}
              hideableStorageKey="Dashboard-Carrier-Collect-Data-Table"
              toggleHideableColumns
              excludeHideableFieldColumns={['title', 'collectItemStateTypeId']}
            />
          </DashboardVariantV2CarrierContentDataTableContainer>
        </Stack>

        {openDialogDetailsOrder && selectedDataForDetailsOrder && (
          <DialogDetailsOrder
            open={openDialogDetailsOrder}
            onClose={handleCloseDialogDetailsOrder}
            enabledChipTitle
            {...selectedDataForDetailsOrder}
          />
        )}

        <DialogConfimation
          open={openConfirmValidateOrder}
          description={messageDialogConfimationValidateOrder}
          onConfirm={handleValidateOrder}
          onCancel={handleCloseConfirmValidateOrder}
          loadingConfirm={loadingValidateOrder}
        />

        {selectedCancelOrder && (
          <DialogCancelPartnerItem
            open={openDialogCancelOrder}
            onClose={handleCloseDialogCancelOrder}
            partnerCompany={selectedCancelOrder}
            onCancelSuccess={reloadDataAndResetSelectedRows}
          />
        )}
      </>
    );
  };

export default DashboardVariantV2CarrierContentCollectTabDataTable;
